import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Megaphone, Paperclip } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Avatar } from '@/shared/components/ui/Avatar';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { useUser } from '@/shared/hooks/useAuth';
import { useAnnouncements, useAnnouncementMutations } from '../hooks/useAnnouncements';
import { AnnouncementFormModal } from '../components/AnnouncementFormModal';
import { AnnouncementDrawer } from '../components/AnnouncementDrawer';
import { AUDIENCE_LABELS, AUDIENCE_OPTIONS, DEPT_LABELS, DEPT_OPTIONS } from '../constants';
import type { Announcement, CreateAnnouncementPayload, UpdateAnnouncementPayload } from '../types';

/** Group label for the company feed — derived from real created_at only. */
function feedGroup(iso: string): 'Today' | 'Yesterday' | 'This Week' | 'Earlier' {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Earlier';
  const diff = Date.now() - t;
  const day = 86_400_000;
  if (diff < day) return 'Today';
  if (diff < 2 * day) return 'Yesterday';
  if (diff < 7 * day) return 'This Week';
  return 'Earlier';
}

const GROUP_ORDER = ['Today', 'Yesterday', 'This Week', 'Earlier'] as const;

function relTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

export function AnnouncementsPage() {
  const user = useUser();
  const role = user?.role ?? '';
  const canCreate = ['superadmin', 'admin', 'manager'].includes(role);
  const canManage = ['superadmin', 'admin'].includes(role); // edit/delete

  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('');
  const [audience, setAudience] = useState('');

  const { data: announcements = [], isLoading, isError } = useAnnouncements();
  const { createAnnouncement, updateAnnouncement, deleteAnnouncement } = useAnnouncementMutations();

  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<Announcement | null>(null);
  const [drawerItem, setDrawerItem] = useState<Announcement | null>(null);
  const [confirmItem, setConfirmItem] = useState<Announcement | null>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return announcements.filter((a) => {
      if (dept && a.target_dept !== dept) return false;
      if (audience && a.target_audience !== audience) return false;
      if (q && ![a.title, a.body, a.author_name].some((f) => (f ?? '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [announcements, search, dept, audience]);

  // Group the feed by time (real created_at), preserving backend recency order.
  const groups = useMemo(() => {
    const map = new Map<string, Announcement[]>();
    for (const a of visible) {
      const g = feedGroup(a.created_at);
      (map.get(g) ?? map.set(g, []).get(g)!).push(a);
    }
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({ label: g, items: map.get(g)! }));
  }, [visible]);

  const handleCreate = () => {
    setEditItem(null);
    setFormOpen(true);
  };
  const handleEdit = (a: Announcement) => {
    setDrawerItem(null);
    setEditItem(a);
    setFormOpen(true);
  };

  const onCreate = (payload: CreateAnnouncementPayload) => {
    createAnnouncement.mutate(payload, {
      onSuccess: () => {
        toast.success('Announcement published.');
        setFormOpen(false);
      },
      onError: () => toast.error('Failed to publish announcement.'),
    });
  };

  const onUpdate = (id: string, payload: UpdateAnnouncementPayload) => {
    updateAnnouncement.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast.success('Announcement updated.');
          setFormOpen(false);
          setEditItem(null);
        },
        onError: () => toast.error('Failed to update announcement.'),
      },
    );
  };

  const handleDelete = () => {
    if (!confirmItem) return;
    deleteAnnouncement.mutate(confirmItem.id, {
      onSuccess: () => {
        toast.success('Announcement deleted.');
        setConfirmItem(null);
        setDrawerItem(null);
      },
      onError: () => toast.error('Failed to delete announcement.'),
    });
  };

  return (
    <div className="flex h-full flex-col gap-4 pb-16">
      <PageHeader
        icon={Megaphone}
        eyebrow="Workspace"
        title="Announcements"
        description={<>{visible.length} announcement{visible.length !== 1 ? 's' : ''}</>}
        actions={
          <>{canCreate && (
                  <Button onClick={handleCreate} size="md">
                    <Plus className="h-4 w-4" />
                    New Announcement
                  </Button>
                )}</>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-64">
          <Input
            name="ann-search"
            placeholder="Search title, body, author…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-48">
          <Select
            name="ann-dept-filter"
            value={dept}
            onChange={(e) => setDept(e.target.value)}
            options={[{ value: '', label: 'All departments' }, ...DEPT_OPTIONS.filter((d) => d.value !== 'all')]}
          />
        </div>
        <div className="w-44">
          <Select
            name="ann-audience-filter"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            options={[{ value: '', label: 'All audiences' }, ...AUDIENCE_OPTIONS.filter((a) => a.value !== 'all')]}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState title="Failed to load announcements" description="Check your connection or try refreshing." />
      ) : visible.length === 0 ? (
        <EmptyState
          title={search || dept || audience ? 'No matching announcements' : 'No announcements yet'}
          description={
            search || dept || audience ? 'Try adjusting your filters.' : 'Published announcements will appear here.'
          }
          action={canCreate && !search && !dept && !audience ? <Button onClick={handleCreate}>New Announcement</Button> : undefined}
        />
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto flex max-w-3xl flex-col gap-6 pb-4">
            {groups.map((group) => (
              <section key={group.label} className="flex flex-col gap-3">
                <div className="sticky top-0 z-10 -mx-1 bg-surface/80 px-1 py-1 backdrop-blur-sm">
                  <span className="text-label-caps uppercase text-on-surface-variant">{group.label}</span>
                </div>
                {group.items.map((a) => (
                  <Card
                    key={a.id}
                    interactive
                    className="flex cursor-pointer gap-3 p-4"
                    onClick={() => setDrawerItem(a)}
                  >
                    <Avatar name={a.author_name || 'Unknown'} size="md" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-data-md font-semibold text-on-surface">
                          {a.author_name || 'Unknown'}
                        </span>
                        <span className="shrink-0 text-body-sm text-on-surface-variant">{relTime(a.created_at)}</span>
                      </div>
                      <h3 className="mt-0.5 line-clamp-1 text-data-lg font-semibold text-on-surface">{a.title}</h3>
                      <p className="mt-1 line-clamp-2 text-body-sm text-on-surface-variant">{a.body}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Badge tone="primary">{DEPT_LABELS[a.target_dept] ?? a.target_dept}</Badge>
                        <Badge tone="info">{AUDIENCE_LABELS[a.target_audience] ?? a.target_audience}</Badge>
                        {a.attachment?.file && (
                          <span className="inline-flex items-center gap-1 text-body-sm text-on-surface-variant">
                            <Paperclip className="h-3.5 w-3.5" /> Attachment
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </section>
            ))}
          </div>
        </div>
      )}

      <AnnouncementFormModal
        open={formOpen}
        announcement={editItem}
        loading={createAnnouncement.isPending || updateAnnouncement.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditItem(null);
        }}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />

      <AnnouncementDrawer
        announcement={drawerItem}
        canManage={canManage}
        deleting={deleteAnnouncement.isPending}
        onClose={() => setDrawerItem(null)}
        onEdit={handleEdit}
        onDelete={setConfirmItem}
      />

      <ConfirmDialog
        open={!!confirmItem}
        title="Delete announcement"
        message={`Delete "${confirmItem?.title ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteAnnouncement.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmItem(null)}
      />
    </div>
  );
}
